import {
  recursiveCamelCaseCipher,
  recursiveSnakeCaseCipher,
  recursiveSnakeCaseCipherTypesafe,
} from "../../postgrestApi";
import {
  Equipment,
  EquipmentCondition,
  EquipmentSpecPayload,
  EquipmentSpecRequestBody,
  UpdateEquipment,
  UpdateEquipmentBody,
  CreateEquipmentBody,
} from "./types";
import { useInvalidateEquipmentQueries } from "./useInvalidateEquipmentQueries";
import { EQUIPMENT_DETAILS_QUERY_KEY } from "./queryKeys";
import { useCurrentAccount } from "@src/backpack-console/CurrentAccountManager";
import FetchClients from "../client";
import { BackpackServiceError, throwBackpackResponseError } from "../errors";
import { useFeatureFlag } from "@src/backpack-console/featureFlags";
import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import {
  EQUIPMENT_SPECS_READS_HEADER_NAME,
  EquipmentRequestHeaders,
  useEquipmentSpecsReadHeaders,
  useEquipmentSpecsWriteHeaders,
} from "./equipmentSpecsHeaders";

const equipment = {
  useQuery,
  usePutPrimaryPhoto,
  useAddToWatchlist,
  usePutEquipmentCondition,
  usePutEquipment,
  usePostEquipment,
};

export default equipment;

// endpoint requires {feature: "Equipment", access: "read"}
const getEquipment = async (
  equipmentId: number,
  headers: EquipmentRequestHeaders
): Promise<Equipment> => {
  const { data, error } = await FetchClients.equipment.GET(
    "/equipment/{equipment_id}",
    {
      params: {
        path: { equipment_id: equipmentId },
      },
      headers,
    }
  );

  if (error) {
    throwBackpackResponseError(error);
  }

  return recursiveCamelCaseCipher(data);
};

function useQuery(equipmentId: number | undefined | null) {
  const equipmentSpecsHeaders = useEquipmentSpecsReadHeaders();
  return useQueryWithAccessCheckLog(
    ["Equipment", "read"],
    (checkFeatureAccessHeader) => {
      if (typeof equipmentId !== "number") {
        throw new Error("Equipment Id is required");
      }
      return getEquipment(equipmentId, {
        ...checkFeatureAccessHeader,
        ...equipmentSpecsHeaders,
      });
    },
    {
      queryKey: [
        EQUIPMENT_DETAILS_QUERY_KEY,
        equipmentId,
        equipmentSpecsHeaders[EQUIPMENT_SPECS_READS_HEADER_NAME],
      ],
      enabled: typeof equipmentId === "number",
      select: (data) => ({
        ...data,
        // match site equipment query, which sorts photos by primary and id descending, limit one
        photoLinks: data.photoLinks?.sort((a, b) => b.id - a.id),
      }),
    }
  );
}

// endpoint requires {feature: "Equipment", access: "edit"}
const putPrimaryPhoto = async (
  fileId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) => {
  const { error } = await FetchClients.d3.PUT(
    "/equipment/set_primary/{file_id}",
    {
      parseAs: "text",
      params: {
        path: { file_id: fileId },
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (error) {
    throw new Error(error);
  }
};

function usePutPrimaryPhoto(siteId: number, equipmentId: number) {
  const { accountId } = useCurrentAccount();
  const invalidateQueries = useInvalidateEquipmentQueries();
  return useMutationWithAccessCheckLog(
    ["Equipment", "edit"],
    async (fileId: number, checkFeatureAccessHeader) => {
      return putPrimaryPhoto(fileId, checkFeatureAccessHeader);
    },
    {
      onSettled: async () => {
        return invalidateQueries({
          equipmentIds: [equipmentId],
          siteId,
          accountId,
        });
      },
    }
  );
}

// endpoint requires {feature: "Equipment", access: "edit"}
async function addToWatchlist(
  equipmentId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { error } = await FetchClients.equipment.PUT(
    "/equipment/watch_list/{equipment_id}",
    {
      params: {
        path: { equipment_id: equipmentId },
      },
      body: true,
      headers: checkFeatureAccessHeader,
    }
  );

  if (error) {
    throw new Error(error);
  }
}

function useAddToWatchlist(siteId: number, equipmentId: number) {
  const { accountId } = useCurrentAccount();
  const invalidateQueries = useInvalidateEquipmentQueries();
  return useMutationWithAccessCheckLog(
    ["Equipment", "edit"],
    async (_variables: void, checkFeatureAccessHeader) => {
      return addToWatchlist(equipmentId, checkFeatureAccessHeader);
    },
    {
      onSettled: async () => {
        return invalidateQueries({
          equipmentIds: [equipmentId],
          siteId,
          accountId,
        });
      },
    }
  );
}

// endpoint requires {feature: "Equipment", access: "edit"}
async function putEquipmentCondition(
  equipmentId: number,
  condition: EquipmentCondition,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { error } = await FetchClients.equipment.PUT(
    "/equipment/condition/{equipment_id}",
    {
      parseAs: "text",
      params: {
        path: { equipment_id: equipmentId },
      },
      body: { equipment_condition: condition },
      headers: checkFeatureAccessHeader,
    }
  );

  if (error) {
    throw new Error(error);
  }
}

function usePutEquipmentCondition(equipmentId: number, siteId: number) {
  const { accountId } = useCurrentAccount();
  const invalidateQueries = useInvalidateEquipmentQueries();
  return useMutationWithAccessCheckLog(
    ["Equipment", "edit"],
    async (condition: EquipmentCondition, checkFeatureAccessHeader) => {
      return putEquipmentCondition(
        equipmentId,
        condition,
        checkFeatureAccessHeader
      );
    },
    {
      onSettled: async () => {
        return invalidateQueries({
          equipmentIds: [equipmentId],
          siteId,
          accountId,
        });
      },
    }
  );
}

// endpoint requires {feature: "Equipment", access: "edit"}
async function putEquipment(
  equipmentId: number,
  params: UpdateEquipment,
  headers: EquipmentRequestHeaders
) {
  const { error } = await FetchClients.equipment.PUT(
    "/equipment/{equipment_id}",
    {
      params: {
        path: {
          equipment_id: equipmentId,
        },
      },
      body: params,
      headers,
    }
  );

  if (error) {
    throwBackpackResponseError(error);
  }
}

// Bractlet HQ is the only site that is safe to edit equipment data on in staging.
const BRACTLET_HQ_SITE_ID = 185;
function checkDangerousEquipmentEditSafety(
  isDangerousEquipmentEditEnabled: boolean,
  siteId: number
) {
  if (!isDangerousEquipmentEditEnabled) {
    if (siteId === BRACTLET_HQ_SITE_ID) {
      return;
    }

    window.alert(
      `WARNING: Creating/Editing equipment in staging will affect production data. Equipment under Bractlet HQ (Site ID 185) should be used for testing.\n\nEditing the current site's equipment is potentially dangerous, but can be performed by enabling the "UNSAFE_enable-dangerous-equipment-edit" feature flag.`
    );

    throw new BackpackServiceError({
      error: {
        message: "Dangerous equipment request is disabled.",
      },
    });
  }
}

function toEquipmentSpecRequestBody(spec: EquipmentSpecPayload) {
  return Object.fromEntries(
    Object.entries(spec).map(([variantKey, variant]) => [
      variantKey,
      recursiveSnakeCaseCipher(variant),
    ])
  ) as EquipmentSpecRequestBody;
}

function usePutEquipment(siteId: number, equipmentId: number) {
  const isDangerousEquipmentEditEnabled =
    useFeatureFlag("UNSAFE_enable-dangerous-equipment-edit", "off") === "on";

  const { accountId } = useCurrentAccount();
  const invalidateQueries = useInvalidateEquipmentQueries();
  const equipmentSpecsHeaders = useEquipmentSpecsWriteHeaders();
  return useMutationWithAccessCheckLog(
    ["Equipment", "edit"],
    async (params: UpdateEquipmentBody, checkFeatureAccessHeader) => {
      checkDangerousEquipmentEditSafety(
        isDangerousEquipmentEditEnabled,
        siteId
      );
      const { equipmentSpec, ...equipmentFields } = params;
      return putEquipment(
        equipmentId,
        {
          ...recursiveSnakeCaseCipherTypesafe(equipmentFields),
          ...(equipmentSpec
            ? { equipment_spec: toEquipmentSpecRequestBody(equipmentSpec) }
            : {}),
        },
        { ...checkFeatureAccessHeader, ...equipmentSpecsHeaders }
      );
    },
    {
      onSettled: async () => {
        return invalidateQueries({
          equipmentIds: [equipmentId],
          siteId,
          accountId,
        });
      },
    }
  );
}

// endpoint requires {feature: "Equipment", access: "edit"}
async function postEquipment(
  siteId: number,
  { equipmentSpec, ...equipmentFields }: CreateEquipmentBody,
  headers: EquipmentRequestHeaders
) {
  const { data, error } = await FetchClients.equipment.POST(
    "/equipment/{site_id}",
    {
      params: {
        path: {
          site_id: siteId,
        },
      },
      body: {
        ...recursiveSnakeCaseCipherTypesafe(equipmentFields),
        ...(equipmentSpec
          ? { equipment_spec: toEquipmentSpecRequestBody(equipmentSpec) }
          : {}),
      },
      parseAs: "json",
      headers,
    }
  );

  if (error) {
    throwBackpackResponseError(error);
  }

  return data;
}
function usePostEquipment(siteId: number) {
  const isDangerousEquipmentEditEnabled =
    useFeatureFlag("UNSAFE_enable-dangerous-equipment-edit", "off") === "on";
  const { accountId } = useCurrentAccount();
  const invalidateQueries = useInvalidateEquipmentQueries();
  const equipmentSpecsHeaders = useEquipmentSpecsWriteHeaders();
  return useMutationWithAccessCheckLog(
    ["Equipment", "edit"],
    async (body: CreateEquipmentBody, checkFeatureAccessHeader) => {
      checkDangerousEquipmentEditSafety(
        isDangerousEquipmentEditEnabled,
        siteId
      );
      return postEquipment(siteId, body, {
        ...checkFeatureAccessHeader,
        ...equipmentSpecsHeaders,
      });
    },
    {
      onSettled: async (data) => {
        return invalidateQueries({
          equipmentIds: data ? [data] : null,
          siteId,
          accountId,
        });
      },
    }
  );
}
